import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { BabylonActions } from '../../babylon';
import BackButton from '../../components/BackButton/BackButton';
import Button from '../../components/Button/Button';
import Container from '../../components/Container/Container';
import HorseTabs from '../../components/HorseTabs/HorseTabs';
import { ShowIcons } from '../../components/HorseTabs/TotalStats/TotalStats';
import Loader from '../../components/Loader/Loader';
import SubHeader from '../../components/SubHeader/SubHeader';
import Paragraph from '../../components/Typography/Paragraph/Paragraph';
import { useJockeys, useSkins } from '../../hooks/stable';
import useResponsive from '../../hooks/useResponsive';
import { useAppDispatch, useAppSelector } from '../../store/reducers';
import { configureHorse, removeConfiguratorSettings, setDeluxeSkinIndex, setHorseSkinIndex, setJockeyIndex, setJockeySkinIndex } from '../../store/reducers/Stable/configurator';
import { getJockey } from '../../store/reducers/Stable/jockeys';
import * as log from '../../utils/log';
import styles from './ConfiguratorView.module.scss';
import JockeyItem from './JockeyItem/JockeyItem';
import SkinItem from './SkinItem/SkinItem';
export var CONFIGURATOR_TAB_KEYS;
(function (CONFIGURATOR_TAB_KEYS) {
    CONFIGURATOR_TAB_KEYS["JOCKEY"] = "Jockey";
    CONFIGURATOR_TAB_KEYS["JOCKEY_SKINS"] = "Jockey skins";
    CONFIGURATOR_TAB_KEYS["HORSE_SKINS"] = "Horse skins";
    CONFIGURATOR_TAB_KEYS["DELUXE_SKINS"] = "Deluxe skins";
})(CONFIGURATOR_TAB_KEYS || (CONFIGURATOR_TAB_KEYS = {}));
const InitUserSelectionState = {
    jockey: '',
    'jockey-skins': '',
    'horse-skins': '',
    'deluxe-skins': ''
};
const ConfiguratorView = ({ canNavigate }) => {
    var _a, _b, _c;
    const isResponsive = useResponsive('xl');
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const configurator = useAppSelector((state) => state.configurator.configurator);
    const pending = useAppSelector((state) => state.configurator.pending);
    const [mount, setMount] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(Date.now());
    const [activeTab, setActiveTab] = useState('Jockey');
    const [showActionButtons, setShowActionButtons] = useState(false);
    const horseDisplayName = useAppSelector((state) => { var _a; return (_a = state.stable.horse) === null || _a === void 0 ? void 0 : _a.static.displayName; }) || '';
    useJockeys();
    const filteredJockeys = useAppSelector((state) => state.jockeys.filteredJockeys);
    const { skins } = useSkins();
    log.debug('ConfiguratorView canNavigate', canNavigate);
    const [jockey, setJockey] = useState(null);
    const [showDefaultAttr, setShowDefaultAttr] = useState(false);
    const onClick = useCallback((value) => {
        setActiveTab(value);
    }, []);
    // Holding user selected assets, skins in array for simultaneous bulk update
    const [userAssets, setUserAssets] = useState(Object.assign({}, InitUserSelectionState));
    const getSkinIndex = (assetName) => {
        var _a;
        return (_a = skins === null || skins === void 0 ? void 0 : skins.findIndex((skin) => skin.asset.assetName === assetName)) !== null && _a !== void 0 ? _a : -1;
    };
    const getJockeyIndex = (assetName) => {
        var _a;
        return (_a = filteredJockeys === null || filteredJockeys === void 0 ? void 0 : filteredJockeys.findIndex((jockey) => jockey.asset.assetName === assetName)) !== null && _a !== void 0 ? _a : -1;
    };
    const getDeluxeValue = (userSel) => userSel['horse-skins'] === userSel['jockey-skins'] ? userSel['jockey-skins'] : '';
    const UpdateConfiguratorState = (_obj, force = false) => {
        if (force) {
        }
        else if (Date.now() - lastUpdate < 300) {
            log.debug('Blocked configurator update: too quick');
            return;
        }
        setLastUpdate(Date.now());
        const jockeyIndex = getJockeyIndex(_obj['jockey']);
        const jockeySkinIndex = getSkinIndex(_obj['jockey-skins']);
        const horseSkinIndex = getSkinIndex(_obj['horse-skins']);
        BabylonActions.SendCurrentStableConfiguratorStateDirectly({
            jockeyIndex,
            jockeySkinIndex,
            horseSkinIndex
        });
    };
    //Updating state with selected user asset ids
    useEffect(() => {
        if (!configurator) {
            // TODO Get the configuration of the first horse by default
            return;
        }
        log.debug('Configurator View useEffect', configurator);
        //Set active tab
        if (location.state.activeTab !== 'Horse') {
            setActiveTab(CONFIGURATOR_TAB_KEYS.JOCKEY_SKINS);
        }
        else {
            setActiveTab(CONFIGURATOR_TAB_KEYS.HORSE_SKINS);
        }
        if (configurator['jockey'] && filteredJockeys) {
            let result = filteredJockeys.find((jockey) => jockey.asset.assetName === configurator['jockey']);
            if (result) {
                dispatch(getJockey(result.asset));
            }
        }
        let _obj = Object.assign(Object.assign({}, userAssets), configurator);
        _obj['deluxe-skins'] = getDeluxeValue(_obj);
        log.debug('ConfiguratorView useEffect', userAssets, configurator, _obj);
        const jockeySkinIndex = getSkinIndex(_obj['jockey-skins']);
        const horseSkinIndex = getSkinIndex(_obj['horse-skins']);
        const jockeyIndex = getJockeyIndex(_obj['jockey']);
        if (jockeyIndex > -1) {
            setJockey(filteredJockeys[jockeyIndex]);
            setShowDefaultAttr(false);
        }
        else {
            setShowDefaultAttr(true);
        }
        dispatch(setJockeyIndex(jockeyIndex));
        dispatch(setJockeySkinIndex(jockeySkinIndex));
        dispatch(setHorseSkinIndex(horseSkinIndex));
        setUserAssets(Object.assign({}, _obj));
        setMount(true);
        UpdateConfiguratorState(_obj, true);
    }, [configurator]);
    const configuratorTabs = useMemo(() => [
        {
            label: t('jockey'),
            value: CONFIGURATOR_TAB_KEYS.JOCKEY
        },
        {
            label: t('jockey_skins'),
            value: CONFIGURATOR_TAB_KEYS.JOCKEY_SKINS
        },
        {
            label: t('horse_skins'),
            value: CONFIGURATOR_TAB_KEYS.HORSE_SKINS
        },
        {
            label: t('deluxe_skins'),
            value: CONFIGURATOR_TAB_KEYS.DELUXE_SKINS
        }
    ], [t]);
    const isHorseSkin = (skin) => ['Horse Skin', 'Deluxe Skin'].includes(skin.type);
    const isJockeySkin = (skin) => ['Jockey Skin', 'Deluxe Skin'].includes(skin.type);
    const isDeluxeSkin = (skin) => 'Deluxe Skin' === skin.type;
    const horseSkins = useMemo(() => (skins === null || skins === void 0 ? void 0 : skins.filter(isHorseSkin)) || [], [skins]);
    const jockeySkins = useMemo(() => (skins === null || skins === void 0 ? void 0 : skins.filter(isJockeySkin)) || [], [skins]);
    const deluxeSkins = useMemo(() => (skins === null || skins === void 0 ? void 0 : skins.filter(isDeluxeSkin)) || [], [skins]);
    // Collecting all change actions to state
    const handleSetJockeySkin = (assetName) => {
        if (userAssets['jockey-skins'] === assetName)
            return;
        setShowActionButtons(true);
        const _obj = Object.assign({}, userAssets);
        _obj['jockey-skins'] = assetName;
        _obj['deluxe-skins'] = getDeluxeValue(_obj);
        dispatch(setJockeySkinIndex(getSkinIndex(assetName)));
        setUserAssets(_obj);
        UpdateConfiguratorState(_obj);
    };
    const handleSetHorseSkin = (assetName) => {
        if (userAssets['horse-skins'] === assetName)
            return;
        setShowActionButtons(true);
        const _obj = Object.assign({}, userAssets);
        _obj['horse-skins'] = assetName;
        _obj['deluxe-skins'] = getDeluxeValue(_obj);
        dispatch(setHorseSkinIndex(getSkinIndex(assetName)));
        setUserAssets(_obj);
        UpdateConfiguratorState(_obj);
    };
    const handleSetDeluxeSkin = (assetName) => {
        if (userAssets['deluxe-skins'] === assetName)
            return;
        setShowActionButtons(true);
        const _obj = Object.assign({}, userAssets);
        _obj['horse-skins'] = assetName;
        _obj['jockey-skins'] = assetName;
        _obj['deluxe-skins'] = assetName;
        dispatch(setDeluxeSkinIndex(getSkinIndex(assetName)));
        setUserAssets(_obj);
        UpdateConfiguratorState(_obj);
    };
    const handleSetJockey = (assetName) => {
        if (userAssets['jockey'] === assetName)
            return;
        const _obj = Object.assign({}, userAssets);
        _obj['jockey'] = assetName;
        const index = getJockeyIndex(assetName);
        dispatch(setJockeyIndex(index));
        setUserAssets(_obj);
        UpdateConfiguratorState(_obj);
        if (index > -1) {
            setJockey(filteredJockeys[index]);
            setShowDefaultAttr(false);
        }
        else {
            setShowDefaultAttr(true);
        }
    };
    const renderView = useMemo(() => {
        var _a, _b, _c;
        return {
            [CONFIGURATOR_TAB_KEYS.JOCKEY]: (_jsx(JockeyItem, { handleUserSelection: handleSetJockey, data: filteredJockeys !== null && filteredJockeys !== void 0 ? filteredJockeys : [], savedUserSelection: userAssets['jockey'], canNavigate: canNavigate })),
            [CONFIGURATOR_TAB_KEYS.JOCKEY_SKINS]: (_jsx(SkinItem, { handleUserSelection: handleSetJockeySkin, data: (_a = skins === null || skins === void 0 ? void 0 : skins.filter(isJockeySkin)) !== null && _a !== void 0 ? _a : [], savedUserSelection: userAssets['jockey-skins'], canNavigate: canNavigate })),
            [CONFIGURATOR_TAB_KEYS.HORSE_SKINS]: (_jsx(SkinItem, { handleUserSelection: handleSetHorseSkin, data: (_b = skins === null || skins === void 0 ? void 0 : skins.filter(isHorseSkin)) !== null && _b !== void 0 ? _b : [], savedUserSelection: userAssets['horse-skins'], canNavigate: canNavigate })),
            [CONFIGURATOR_TAB_KEYS.DELUXE_SKINS]: (_jsx(SkinItem, { handleUserSelection: handleSetDeluxeSkin, data: (_c = skins === null || skins === void 0 ? void 0 : skins.filter(isDeluxeSkin)) !== null && _c !== void 0 ? _c : [], savedUserSelection: userAssets['deluxe-skins'], canNavigate: canNavigate }))
        };
    }, [jockeySkins, filteredJockeys, horseSkins, deluxeSkins, mount, userAssets, canNavigate]);
    //Compares Fetched Configurator values with Current changes
    //If change action on assets has been performed then updates
    const handleSave = () => {
        const horseAssetName = location.state.id;
        Object.keys(userAssets).forEach((key) => {
            userAssets[key] === ''
                ? dispatch(removeConfiguratorSettings({
                    horseAssetName,
                    type: key
                }))
                : dispatch(configureHorse({
                    assetName: userAssets[key],
                    type: key,
                    horseAssetName
                }));
        });
        navigate(`/stables/race/horse/${horseAssetName}`);
        BabylonActions.StablesGoToHorseView();
    };
    const handleCancel = () => {
        navigate(`/stables/race/horse/${location.state.id}`);
        BabylonActions.StablesGoToHorseView();
    };
    const CancelButton = () => (_jsx(Button, { size: 'xs', color: 2, height: 'sm', text: t('cancel'), onClick: handleCancel }));
    const SaveButton = () => pending ? (_jsx(Button, { size: 'xs', height: 'sm', text: "", icon: _jsx(Loader, {}) })) : (_jsx(Button, { size: 'xs', color: 1, onClick: handleSave, height: 'sm', text: t('save') }));
    return (_jsxs("div", { children: [!isResponsive && (_jsxs(SubHeader, Object.assign({ className: styles.subheader, background: true, buttons: _jsxs("div", Object.assign({ className: cn(styles.buttons, 'd-flex align-items-center configure-btn') }, { children: [_jsx(CancelButton, {}), _jsx(SaveButton, {})] })), title: horseDisplayName }, { children: [_jsx(BackButton, { className: styles.arrowLeft, extraCallback: BabylonActions.StablesGoToHorseView }), _jsxs("div", Object.assign({ className: styles.wrapper }, { children: [_jsxs("div", Object.assign({ className: styles.box }, { children: [_jsx(Paragraph, { size: 1, color: 'grey', title: t('Wisdom') }), ShowIcons(!showDefaultAttr ? (_a = jockey === null || jockey === void 0 ? void 0 : jockey.wisdom) !== null && _a !== void 0 ? _a : 1 : 1)] })), _jsxs("div", Object.assign({ className: styles.box }, { children: [_jsx(Paragraph, { title: t('Courage'), size: 1, color: 'grey' }), ShowIcons(!showDefaultAttr ? (_b = jockey === null || jockey === void 0 ? void 0 : jockey.courage) !== null && _b !== void 0 ? _b : 1 : 1)] })), _jsxs("div", Object.assign({ className: styles.box }, { children: [_jsx(Paragraph, { size: 1, color: 'grey', title: t('Determination') }), ShowIcons(!showDefaultAttr ? (_c = jockey === null || jockey === void 0 ? void 0 : jockey.determination) !== null && _c !== void 0 ? _c : 1 : 1)] }))] }))] }))), _jsx("div", { className: styles.jockey }), _jsxs("div", Object.assign({ className: styles.tabBox }, { children: [_jsx("div", Object.assign({ className: styles.elem }, { children: _jsx(Container, { children: _jsx(HorseTabs, { activeTab: activeTab, onClick: onClick, className: styles.tabs, tabs: configuratorTabs, jockey: false }) }) })), renderView[activeTab]] })), _jsx(ToastContainer, {})] }));
};
export default ConfiguratorView;
